import {
  computed,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue';
import getAccountCurrentPositionsV2 from '@gpx-spa/queries/src/getAccountCurrentPositionsV2';
import getAccountShipmentRoutesBatch from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch';
import getAccountShipmentsTracksBatch from '@gpx-spa/queries/src/getAccountShipmentsTracksBatch';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';

export type OperationalMapDataFetchOptions = {
  /**
   * When false, tracks/routes/positions batch queries stay disabled (e.g. wait until
   * parent list query finished so shipment ids match the current page params).
   */
  operationalBatchEnabled?: MaybeRefOrGetter<boolean>;
  /** When false, shipments-tracks API is not called (Track layer off). */
  tracksFetchEnabled?: MaybeRefOrGetter<boolean>;
  /** When false, shipment-routes API is not called (Route lines and Corridor off). */
  routesFetchEnabled?: MaybeRefOrGetter<boolean>;
  /** When false, current-positions API is not called (Current device positions off). */
  positionsFetchEnabled?: MaybeRefOrGetter<boolean>;
};

/**
 * Tracks / routes / current positions for the operational map.
 * Shipment rows come from the parent (list or detail); this composable does not fetch shipment entities.
 */
export function useOperationalMapData(
  accountId: MaybeRefOrGetter<number>,
  shipments: MaybeRefOrGetter<V2ShipmentDetail[]>,
  fetchOptions?: OperationalMapDataFetchOptions,
) {
  const batchShipmentIds = computed(() => {
    const raw = toValue(shipments);
    const rows = Array.isArray(raw) ? raw : [];
    const ids = rows.map((r) => r.id).filter((id) => id > 0);
    return [...new Set(ids)].sort((a, b) => a - b);
  });

  const deviceIds = computed(() => {
    const raw = toValue(shipments);
    const rows = Array.isArray(raw) ? raw : [];
    const set = new Set<number>();
    for (const r of rows) {
      for (const id of r.device_ids ?? []) {
        if (typeof id === 'number' && Number.isFinite(id)) {
          set.add(id);
        }
      }
    }
    return [...set].sort((a, b) => a - b);
  });

  const aid = computed(() => toValue(accountId));

  const operationalBatchAllowed: ComputedRef<boolean> = computed(() =>
    fetchOptions?.operationalBatchEnabled === undefined
      ? true
      : toValue(fetchOptions.operationalBatchEnabled),
  );

  const tracksFetchEnabled: ComputedRef<boolean> = computed(() =>
    fetchOptions?.tracksFetchEnabled === undefined
      ? true
      : toValue(fetchOptions.tracksFetchEnabled),
  );
  const routesFetchEnabled: ComputedRef<boolean> = computed(() =>
    fetchOptions?.routesFetchEnabled === undefined
      ? true
      : toValue(fetchOptions.routesFetchEnabled),
  );
  const positionsFetchEnabled: ComputedRef<boolean> = computed(() =>
    fetchOptions?.positionsFetchEnabled === undefined
      ? true
      : toValue(fetchOptions.positionsFetchEnabled),
  );

  const tracksQuery = getAccountShipmentsTracksBatch(aid, batchShipmentIds, {
    enabled: computed(
      () => operationalBatchAllowed.value && tracksFetchEnabled.value,
    ),
  });
  const routesQuery = getAccountShipmentRoutesBatch(aid, batchShipmentIds, {
    enabled: computed(
      () => operationalBatchAllowed.value && routesFetchEnabled.value,
    ),
  });
  const positionsQuery = getAccountCurrentPositionsV2(aid, deviceIds, {
    enabled: computed(
      () => operationalBatchAllowed.value && positionsFetchEnabled.value,
    ),
  });

  return {
    tracksQuery,
    routesQuery,
    positionsQuery,
    batchShipmentIds,
    deviceIds,
    operationalBatchAllowed,
    tracksFetchEnabled,
    routesFetchEnabled,
    positionsFetchEnabled,
  };
}
